// ─── Spec design tokens (consumed via CSS classes defined in index.css) ───────
//   bg:       #000000   → bg-black
//   card:     #18181B   → bg-[#18181B]  / .mp-card
//   border:   #27272A   → border-[#27272A]
//   accent:   #FBBF24   → text-yellow-400 / bg-yellow-400
//   text:     #FFFFFF   → text-white
//   muted:    #E4E4E7   → text-[#E4E4E7]

// ─── Tailwind class constants ─────────────────────────────────────────────────
export const inputCls =
  'mp-input';                   // defined in index.css

export const labelCls =
  'mp-label';                   // defined in index.css

export const thCls =
  'mp-th';                      // defined in index.css

export const tdCls =
  'mp-td';                      // defined in index.css

// ─── StatusBadge ─────────────────────────────────────────────────────────────
const STATUS_CLS: Record<string, string> = {
  pending:   'mp-badge mp-badge-pending',
  confirmed: 'mp-badge mp-badge-confirmed',
  completed: 'mp-badge mp-badge-completed',
  cancelled: 'mp-badge mp-badge-cancelled',
  refunded:  'mp-badge mp-badge-refunded',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={STATUS_CLS[status] ?? 'mp-badge bg-[#27272A] text-[#A1A1AA]'}>
      {status}
    </span>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
export function StatCard({
  label, value, color, sub,
}: {
  label: string; value: number | string; color?: string; sub?: string;
}) {
  return (
    <div className="mp-card p-5 shadow-sm">
      <p className="text-[#71717A] text-xs font-semibold uppercase tracking-wider mb-2">
        {label}
      </p>
      <p
        className="text-3xl font-bold text-white"
        style={color ? { color } : undefined}
      >
        {value}
      </p>
      {sub && <p className="text-[#52525B] text-xs mt-1">{sub}</p>}
    </div>
  );
}

// ─── AdminTable ───────────────────────────────────────────────────────────────
export function AdminTable({
  headers, children, empty,
}: {
  headers: string[];
  children: React.ReactNode;
  empty: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#27272A]">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-[#09090B]">
          <tr>
            {headers.map(h => (
              <th key={h} className="mp-th px-4 pt-4 pb-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-[#18181B]">{children}</tbody>
      </table>
      {empty && (
        <div className="bg-[#18181B] py-16 text-center">
          <p className="text-[#3F3F46] text-sm">No records yet</p>
        </div>
      )}
    </div>
  );
}

// ─── SectionsEditor ───────────────────────────────────────────────────────────
export type SectionDraft = {
  name:            string;
  durationMinutes: number;
  order:           number;
  videoUrl?:       string | null;
};

export function SectionsEditor({
  sections, onChange,
}: {
  sections: SectionDraft[];
  onChange: (s: SectionDraft[]) => void;
}) {
  const update = (i: number, patch: Partial<SectionDraft>) => {
    const next = [...sections];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () =>
    onChange([
      ...sections,
      { name: '', durationMinutes: 30, order: sections.length, videoUrl: '' },
    ]);
  const remove = (i: number) =>
    onChange(sections.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="mp-label">Sections</label>
        <button
          type="button"
          onClick={add}
          className="text-yellow-400 text-xs font-bold hover:text-yellow-300 transition-colors"
        >
          + Add section
        </button>
      </div>

      <div className="space-y-4">
        {sections.map((s, i) => (
          <div key={i} className="border border-[#27272A] rounded-2xl p-4 bg-[#0d0d10] relative">
            {/* Header with section number + remove button */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest text-[#71717A] font-bold">
                Section {i + 1}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label="Remove section"
                className="text-[#52525B] hover:text-red-400 text-xs flex items-center gap-1 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Remove
              </button>
            </div>

            {/* Name (wide) + Length (narrow) on the same row */}
            <div className="grid sm:grid-cols-[1fr_88px] gap-3 mb-3">
              <div>
                <label className="mp-label">Name</label>
                <input
                  value={s.name}
                  onChange={e => update(i, { name: e.target.value })}
                  placeholder="e.g. Chapter 1 — Introduction"
                  className="mp-input"
                />
              </div>
              <div>
                <label className="mp-label">Min</label>
                <input
                  type="number"
                  min={0}
                  value={s.durationMinutes}
                  onChange={e => update(i, { durationMinutes: +e.target.value })}
                  className="mp-input"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Video link — full width on its own row */}
            <div>
              <label className="mp-label">Video link</label>
              <input
                type="text"
                inputMode="url"
                value={s.videoUrl ?? ''}
                onChange={e => update(i, { videoUrl: e.target.value })}
                placeholder="YouTube, Vimeo, or direct .mp4 URL"
                className="mp-input"
              />
              <p className="text-[10px] text-[#52525B] mt-1">
                Paste a full URL. Leave blank if there's no video yet.
              </p>
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <p className="text-[#3F3F46] text-xs py-2">
            No sections — click "+ Add section"
          </p>
        )}
      </div>
    </div>
  );
}

// ─── CSV export ───────────────────────────────────────────────────────────────
export function exportCsv(data: Record<string, unknown>[], filename: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows    = data.map(row =>
    headers.map(h => JSON.stringify(row[h] ?? '')).join(',')
  );
  const csv  = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a    = document.createElement('a');
  a.href     = URL.createObjectURL(blob);
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── AdminSectionHeader ───────────────────────────────────────────────────────
export function AdminSectionHeader({
  title, count, onExport, exportLabel = 'Export CSV',
}: {
  title: string; count: number; onExport?: () => void; exportLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-3xl font-bold text-white tracking-tight">{title}</h2>
        <p className="text-[#71717A] text-lg mt-1">
          {count} record{count !== 1 ? 's' : ''}
        </p>
      </div>
      {onExport && (
        <button
          onClick={onExport}
          className="flex items-center gap-2 bg-[#18181B] hover:bg-[#27272A]
                     border border-[#27272A] text-[#E4E4E7] hover:text-white
                     text-sm font-semibold px-4 py-2 rounded-2xl transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {exportLabel}
        </button>
      )}
    </div>
  );
}

// ─── InlineError ──────────────────────────────────────────────────────────────
export function InlineError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24"
           stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {message}
    </p>
  );
}
